import './AttendanceTable.scss'
import { Column } from 'primereact/column'
import { useState } from 'react'
import GenericDataTable from '../generic-datatable/GenericDataTable'
import { TABLE_INTERVAL_WEEKLY, TABLE_INTERVAL_MONTHLY, ALL_COLUMNS, COLUMN_FIELDS, DisplayStatus } from './AttendanceTableData'
import { ReactComponent as NoDataImg } from '../../assets/NoData.svg'
import { ReactComponent as AlertCircle } from './assets/Alert-circle.svg'
import { ReactComponent as PencilIcon } from './assets/PencilIcon.svg'
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns'
import { useGetAllClockInClockoutDataQuery } from '../../services/attendance-portal/clockInOut'
import { Skeleton } from 'primereact/skeleton'
import { Button } from 'primereact/button'
import RegularizeModal from './RegularizeModal'
import { useToggle } from '../../hooks'
import { Tooltip } from 'primereact/tooltip'
import { useSelector } from 'react-redux'
import { selectIsUserLoggedIn } from '../../pages/login-page/LoginAuthSlice'

// Used when there is no rows in the table
function NoDataDisplay() {
  return (
    <div className='text-center py-6'>
      <NoDataImg className='' />
    </div>
  )
}

export const StatusColumn = ({ docId, isRegularizationAllowed = true }) => {
  const [showRegularizeModal, setShowRegularizeModal] = useToggle(false)

  if (!isRegularizationAllowed) {
    return (
      <div className="flex justify-content-start align-items-center relative">
        <AlertCircle className="mr-2 AlertCircle" title="You have exhausted the monthly limit for Regularization Requests" />
      </div>
    )
  }
  return (
    <>
      <RegularizeModal onShow={showRegularizeModal} onHide={setShowRegularizeModal} docId={docId} />
      <div className="flex justify-content-start align-items-center">
        <AlertCircle className="mr-2 AlertCircle" />
        <Button icon={<PencilIcon className="PencilSVG" />} className="p-button-rounded p-button-text" aria-label="Regularize" title='Regularize'
          onClick={setShowRegularizeModal} />
      </div>
    </>
  )
}

const AttendanceTable = () => {
  const [activeState, setActiveState] = useState(TABLE_INTERVAL_WEEKLY)
  const startDateOfWeek = startOfWeek(new Date())
  const endDateOfWeek = endOfWeek(new Date())
  const startDateOfMonth = startOfMonth(new Date())
  const endDateOfMonth = endOfMonth(new Date())
  const weeklyTooltipText = `${format(startDateOfWeek, "do, MMM yy")} - ${format(endDateOfWeek, "do, MMM yy")}`
  const monthlyTooltipText = format(endDateOfMonth, "MMMM, yy")
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn)
  const [clockInTableData, setClockInTableData] = useState({
    startDate: startDateOfWeek.getTime(),
    endDate: endDateOfWeek.getTime()
  })
  const { isLoading, data: attendanceTableDataFromBackend } = useGetAllClockInClockoutDataQuery(clockInTableData, { skip: !isUserLoggedIn })
  const handleTimePeriod = (field) => {
    if (activeState === field) return;
    setActiveState(field)
    if (field === TABLE_INTERVAL_WEEKLY) {
      setClockInTableData({
        startDate: startDateOfWeek.getTime(),
        endDate: endDateOfWeek.getTime()
      })
    } else if (field === TABLE_INTERVAL_MONTHLY) {
      setClockInTableData({
        startDate: startDateOfMonth.getTime(),
        endDate: endDateOfMonth.getTime()
      })
    }
  }


  const bodyTemplate = (data, column) => {
    if (isLoading) {
      return <Skeleton height="2rem" className='SkeletonColor' />
    }
    if (column.field === COLUMN_FIELDS.CLOCK_IN) {
      if (!data[column.field]) {
        return null
      }
      return format(new Date(data[column.field]), 'h:mm a')
    }
    if (column.field === COLUMN_FIELDS.DATE) {
      return format(new Date(data[COLUMN_FIELDS.CLOCK_IN]), 'dd MMM, EEEE')
    }
    if (column.field === COLUMN_FIELDS.WORKING_HOURS) {
      if (!data[column.field]) {
        return null
      }
      const _tempWorkingHours = Number(data[column.field])
      let hours = Math.floor(_tempWorkingHours / 60)
      let minutes = _tempWorkingHours % 60
      return <>{hours} hrs  {minutes} mins</>
    }
    if (column.field === COLUMN_FIELDS.CLOCK_OUT) {
      if (!data[column.field]) {
        return <>- - -</>
      }
      return format(new Date(data[column.field]), 'h:mm a')
    }
    if (column.field === COLUMN_FIELDS.REGULARIZATION_STATUS) {
      const docId = data['_id']
      return DisplayStatus(data[column.field], docId)

    }
    return data[column.field]
  }

  return (
    <div className='AttendanceTable p-3 overflow-auto'>
      <Tooltip target=".WeeklyBtn" className='text-sm' position='top' />
      <Tooltip target=".MonthlyBtn" className='text-sm' position='top' />
      <div className="grid no-gutter justify-content-between align-items-center mb-2 ">
        <div className=" col-12 md:col-6 ">
          <h6 className="font-medium text-base md:text-lg p-1 m-0 text-white">Attendance</h6>
        </div>
        <div className="col md:col-fixed">
          <div className="TimeSetButtons  flex flex-row justify-content-between align-items-center p-1   text-sm cursor-pointer">
            <div className={`WeeklyBtn ${activeState === TABLE_INTERVAL_WEEKLY && 'Active'} py-2 px-3 text-sm `} onClick={() => handleTimePeriod(TABLE_INTERVAL_WEEKLY)} data-pr-tooltip={weeklyTooltipText}> This Week </div>
            <div className={`MonthlyBtn ${activeState === TABLE_INTERVAL_MONTHLY && 'Active'} py-2 px-3 text-sm`} onClick={() => handleTimePeriod(TABLE_INTERVAL_MONTHLY)} data-pr-tooltip={monthlyTooltipText}>This Month</div>
          </div>
        </div>
      </div>
      <GenericDataTable spacedRows={true} value={attendanceTableDataFromBackend} responsiveLayout="scroll" rows={8} size='small' align='center'
        emptyMessage={<NoDataDisplay />} cellClassName='border-none px-2 ' tableClassName='md:text-sm text-xs font-normal white-space-nowrap text-overflow-ellipsis overflow-x-hidden' scrollHeight="25rem" stripedRows	 >
        <Column header={ALL_COLUMNS.DATE} field={COLUMN_FIELDS.DATE} align='center' body={bodyTemplate} />
        <Column header={ALL_COLUMNS.CLOCK_IN} field={COLUMN_FIELDS.CLOCK_IN} align='center' body={bodyTemplate} />
        <Column header={ALL_COLUMNS.CLOCK_OUT} field={COLUMN_FIELDS.CLOCK_OUT} align='center' body={bodyTemplate} />
        <Column header={ALL_COLUMNS.WORKING_HOURS} field={COLUMN_FIELDS.WORKING_HOURS} align='center' body={bodyTemplate} />
        <Column header={ALL_COLUMNS.STATUS} field={COLUMN_FIELDS.REGULARIZATION_STATUS} align='center' body={bodyTemplate} />
      </GenericDataTable>
    </div>
  )
}

export default AttendanceTable